import { useEffect, useRef, useState } from "react";
import { NavLink } from "react-router-dom";
import { ChevronRight, Diamond, HelpCircle, Home, Menu, PanelLeftClose, PanelLeftOpen, UserRound, X } from "lucide-react";

const RailTip = ({ children }) => (
  <span className="pointer-events-none absolute left-[56px] z-[130] whitespace-nowrap rounded-lg border border-[#1E293B] bg-[#111520] px-2.5 py-1.5 text-[0.6875rem] font-medium text-slate-200 opacity-0 shadow-xl transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100">
    {children}
  </span>
);

const Badge = ({ count }) => count > 0 ? (
  <span className="ml-auto min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[0.625rem] font-bold font-mono-data inline-flex items-center justify-center">
    {count > 99 ? "99+" : count}
  </span>
) : null;

export default function HybridSidebar({ groups, pathname, search, collapsed, setCollapsed, inqBadge, user, navigate, mobileOpen, setMobileOpen }) {
  const itemIsActive = (item) => {
    const [path, query] = item.to.split("?");
    const pathMatches = pathname === path || pathname.startsWith(path + "/");
    if (!pathMatches) return false;
    const activeView = new URLSearchParams(search || "").get("view");
    if (query) return activeView === new URLSearchParams(query).get("view");
    if (path === "/sales" && activeView === "balances") return false;
    return !groups.some(group => group.items.some(other => {
      const otherPath = other.to.split("?")[0];
      return otherPath.length > path.length && otherPath.startsWith(path + "/") &&
        (pathname === otherPath || pathname.startsWith(otherPath + "/"));
    }));
  };
  const activeGroup = groups.find((group) => group.title && group.items.some(itemIsActive));
  const titledGroups = groups.filter((group) => group.title);
  const primaryGroups = titledGroups.filter((group) => group.title !== "Admin");
  const adminGroup = titledGroups.find((group) => group.title === "Admin");
  const [section, setSection] = useState(activeGroup?.title || titledGroups[0]?.title || "");
  const [hoverExpanded, setHoverExpanded] = useState(false);
  const hoverTimer = useRef(null);
  const selected = titledGroups.find((group) => group.title === section) || titledGroups[0];
  const expanded = !collapsed || hoverExpanded;

  useEffect(() => {
    if (activeGroup?.title) setSection(activeGroup.title);
  }, [activeGroup?.title]);

  useEffect(() => {
    const close = (event) => {
      if (event.key === "Escape") {
        setHoverExpanded(false);
        setMobileOpen(false);
      }
    };
    window.addEventListener("keydown", close);
    return () => window.removeEventListener("keydown", close);
  }, [setMobileOpen]);

  useEffect(() => () => clearTimeout(hoverTimer.current), []);

  const openHover = () => {
    if (!collapsed) return;
    clearTimeout(hoverTimer.current);
    hoverTimer.current = setTimeout(() => setHoverExpanded(true), 100);
  };
  const closeHover = () => {
    clearTimeout(hoverTimer.current);
    setHoverExpanded(false);
  };
  const choose = (title) => {
    setSection(title);
    if (collapsed && !hoverExpanded) setCollapsed(false);
  };

  const content = (mobile = false) => (
    <aside
      data-app-sidebar
      onMouseEnter={mobile ? undefined : openHover}
      onMouseLeave={mobile ? undefined : closeHover}
      className={`erp-sidebar ${mobile ? "w-[292px]" : collapsed ? "w-[72px]" : "w-[292px]"} h-full flex bg-[#111520] border-r border-[#1E293B] text-slate-100 transition-[width] duration-200 overflow-visible relative z-[100]`}
    >
      <div className="erp-sidebar-rail w-[72px] flex-shrink-0 h-full border-r border-[#1E293B] bg-[#0A0D14] flex flex-col items-center py-4">
        <button aria-label="Open Milora menu" onClick={() => setCollapsed(false)} className="group relative w-10 h-10 rounded-xl bg-[#7DD3FC] text-[#0A0D14] flex items-center justify-center shadow-lg">
          <Diamond className="w-[19px]" strokeWidth={1.8} /><RailTip>Open Milora menu</RailTip>
        </button>
        <div className="w-8 h-px bg-[#1E293B] my-4" />
        <NavLink to="/dashboard" className={({ isActive }) => `group relative w-11 h-11 rounded-xl flex items-center justify-center transition-colors ${isActive ? "bg-[#7DD3FC]/10 text-[#7DD3FC] after:absolute after:-right-[14px] after:top-3 after:bottom-3 after:w-[3px] after:rounded-l-full after:bg-[#7DD3FC]" : "text-slate-500 hover:text-slate-100 hover:bg-[#1A202E]"}`}>
          <Home className="w-[18px]" /><RailTip>Dashboard</RailTip>
        </NavLink>
        <div className="mt-2 space-y-1.5">
          {primaryGroups.map((group) => {
            const current = section === group.title && expanded;
            const hasActive = group.items.some(itemIsActive);
            return (
              <button aria-label={group.title} aria-pressed={current} key={group.title} onMouseEnter={() => { setSection(group.title); openHover(); }} onFocus={() => { setSection(group.title); openHover(); }} onClick={() => choose(group.title)} className={`group relative w-11 h-11 rounded-xl flex items-center justify-center transition-all ${current ? "bg-[#7DD3FC] text-[#0A0D14] shadow-lg" : hasActive ? "bg-[#7DD3FC]/10 text-[#7DD3FC]" : "text-slate-500 hover:text-slate-100 hover:bg-[#1A202E]"} ${hasActive ? "after:absolute after:-right-[14px] after:top-3 after:bottom-3 after:w-[3px] after:rounded-l-full after:bg-[#7DD3FC]" : ""}`}>
                <group.icon className="w-[18px]" strokeWidth={1.75} />
                {group.items.some((item) => item.testid === "nav-inquiries") && inqBadge > 0 && <span className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full bg-red-500 ring-2 ring-[#0A0D14]" />}
                <RailTip>{group.title}</RailTip>
              </button>
            );
          })}
        </div>
        <div className="mt-auto space-y-1.5">
          {adminGroup && (() => {
            const current = section === adminGroup.title && expanded;
            const hasActive = adminGroup.items.some(itemIsActive);
            return <button aria-label="Admin" aria-pressed={current} onMouseEnter={() => { setSection(adminGroup.title); openHover(); }} onFocus={() => { setSection(adminGroup.title); openHover(); }} onClick={() => choose(adminGroup.title)} className={`group relative w-11 h-11 rounded-xl flex items-center justify-center transition-colors ${current ? "bg-[#7DD3FC] text-[#0A0D14]" : hasActive ? "bg-[#7DD3FC]/10 text-[#7DD3FC]" : "text-slate-500 hover:text-slate-100 hover:bg-[#1A202E]"}`}><adminGroup.icon className="w-[18px]" /><RailTip>Admin</RailTip></button>;
          })()}
          <button aria-label="Help center" onClick={() => navigate("/help")} className="group relative w-11 h-11 rounded-xl text-slate-500 hover:text-slate-100 hover:bg-[#1A202E] flex items-center justify-center"><HelpCircle className="w-[18px]" /><RailTip>Help center</RailTip></button>
          {!mobile && <button aria-label={collapsed ? "Pin menu open" : "Collapse menu"} onClick={() => { setCollapsed((value) => !value); setHoverExpanded(false); }} className="group relative w-11 h-11 rounded-xl border border-[#1E293B] text-slate-400 hover:text-[#7DD3FC] hover:border-[#7DD3FC]/40 flex items-center justify-center">{collapsed ? <PanelLeftOpen className="w-[18px]" /> : <PanelLeftClose className="w-[18px]" />}<RailTip>{collapsed ? "Pin menu open" : "Collapse menu"}</RailTip></button>}
        </div>
      </div>

      {(mobile || expanded) && selected && (
        <div className={`erp-sidebar-panel z-[110] w-[220px] flex-shrink-0 h-full flex flex-col bg-[#111520] ${!mobile && collapsed ? "absolute left-[72px] top-0 shadow-[18px_0_40px_rgba(0,0,0,.4)] border-r border-[#1E293B]" : "relative"}`}>
          <div className="h-[69px] px-5 flex items-center border-b border-[#1E293B] shrink-0">
            <div><p className="font-display text-[1.125rem] tracking-[0.08em] leading-none">MILORA</p><p className="font-mono-data text-[0.5625rem] uppercase tracking-[0.19em] text-slate-500 mt-2">Jewelry ERP</p></div>
            <button aria-label={mobile ? "Close navigation" : collapsed ? "Pin menu open" : "Collapse menu"} onClick={() => {
              if (mobile) setMobileOpen(false);
              else if (collapsed) {
                setCollapsed(false);
                setHoverExpanded(false);
              } else {
                setCollapsed(true);
                setHoverExpanded(false);
              }
            }} className="ml-auto w-8 h-8 rounded-lg text-slate-500 hover:text-slate-100 hover:bg-[#1A202E] flex items-center justify-center">{mobile ? <X className="w-4" /> : collapsed ? <PanelLeftOpen className="w-4" /> : <X className="w-4" />}</button>
          </div>
          <div className="px-4 pt-5 pb-3"><div className="flex items-center gap-2.5"><selected.icon className="w-[17px] text-[#7DD3FC]" /><p className="text-[0.875rem] font-semibold tracking-wide">{selected.title}</p></div><p className="text-[0.625rem] text-slate-500 mt-1.5">Choose a workspace</p></div>
          <nav aria-label={selected.title + " navigation"} className="flex-1 overflow-y-auto px-3 space-y-1 sidebar-scroll">
            {selected.items.map((item) => (
              <NavLink aria-current={itemIsActive(item) ? "page" : false} key={item.to} to={item.to} data-testid={item.testid} onClick={() => mobile && setMobileOpen(false)} className={() => `relative w-full min-h-10 px-3 py-2 rounded-xl flex items-center gap-2.5 text-left transition-colors ${itemIsActive(item) ? "bg-[#7DD3FC]/10 text-[#7DD3FC] font-semibold before:absolute before:left-0 before:top-2 before:bottom-2 before:w-[3px] before:rounded-r-full before:bg-[#7DD3FC]" : "text-slate-400 hover:text-slate-100 hover:bg-[#1A202E]"}`}>
                <item.icon className="w-[15px] flex-shrink-0" strokeWidth={1.75} /><span className="text-[0.78125rem] leading-tight">{item.label}</span>{item.testid === "nav-inquiries" && <Badge count={inqBadge} />}<ChevronRight className="ml-auto w-3.5 opacity-30" />
              </NavLink>
            ))}
          </nav>
          <div className="p-3 border-t border-[#1E293B]"><div className="p-2.5 rounded-xl bg-[#1A202E] border border-[#1E293B] flex items-center gap-2.5"><div className="w-8 h-8 rounded-full bg-[#0A0D14] text-[#7DD3FC] flex items-center justify-center"><UserRound className="w-3.5" /></div><div className="min-w-0"><p className="text-[0.6875rem] font-semibold truncate">{user?.name || "Milora Jewelry"}</p><p className="text-[0.5625rem] text-slate-500 mt-0.5 capitalize">{user?.role || "workspace"}</p></div></div></div>
        </div>
      )}
    </aside>
  );

  return <>{<div className="hidden md:block sticky top-0 z-[100] h-screen flex-shrink-0">{content(false)}</div>}<button aria-label="Open navigation" onClick={() => setMobileOpen(true)} className="md:hidden fixed left-3 top-3 z-40 w-10 h-10 rounded-lg border border-[#1E293B] bg-[#111520] text-slate-300 flex items-center justify-center"><Menu className="w-5" /></button>{mobileOpen && <div className="md:hidden fixed inset-0 z-[120] flex"><button aria-label="Close navigation" className="absolute inset-0 bg-black/70" onClick={() => setMobileOpen(false)} />{content(true)}</div>}</>;
}
